import React, { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { Button, Field, Input } from './ui';
import { AuthAlert, AuthLayout } from './auth/AuthLayout';

interface SignupProps {
  onSwitchToLogin: () => void;
}

const PASSWORD_HINT = 'At least 8 characters, with a number and an uppercase letter';

export function Signup({ onSwitchToLogin }: SignupProps) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [confirmError, setConfirmError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [showPending, setShowPending] = useState(false);
  const { signup } = useAuth();

  const validatePassword = (password: string): string | null => {
    if (password.length < 8) {
      return 'Password must be at least 8 characters';
    }
    if (!/\d/.test(password)) {
      return 'Password must contain at least 1 number';
    }
    if (!/[A-Z]/.test(password)) {
      return 'Password must contain at least 1 uppercase letter';
    }
    return null;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setPasswordError('');
    setConfirmError('');

    const invalid = validatePassword(password);
    if (invalid) {
      setPasswordError(invalid);
      return;
    }

    if (password !== confirmPassword) {
      setConfirmError('Passwords do not match');
      return;
    }

    setIsLoading(true);

    try {
      const result = await signup(email, password);
      if (result.approval_status === 'pending') {
        setShowPending(true);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Signup failed');
    } finally {
      setIsLoading(false);
    }
  };

  if (showPending) {
    return (
      <AuthLayout title="Awaiting clearance">
        <AuthAlert tone="warning">
          Your account is registered and waiting for an administrator to approve it.
          You can sign in as soon as it is approved.
        </AuthAlert>
        <Button variant="primary" onClick={onSwitchToLogin} style={{ width: '100%' }}>Back to sign in</Button>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout
      title="Create account"
      subtitle="An administrator approves new accounts before first sign-in."
      error={error}
      footer={
        <>
          Already have an account?{' '}
          <button type="button" className="auth-link" onClick={onSwitchToLogin}>Sign in</button>
        </>
      }
    >
      <form className="auth-form" onSubmit={handleSubmit}>
        <Field label="Email">
          {p => (
            <Input
              {...p}
              type="email"
              autoComplete="email"
              autoFocus
              value={email}
              onChange={e => setEmail(e.target.value)}
              required
            />
          )}
        </Field>
        <Field label="Password" hint={PASSWORD_HINT} error={passwordError}>
          {p => (
            <Input
              {...p}
              type="password"
              autoComplete="new-password"
              value={password}
              onChange={e => { setPassword(e.target.value); setPasswordError(''); }}
              required
            />
          )}
        </Field>
        <Field label="Confirm password" error={confirmError}>
          {p => (
            <Input
              {...p}
              type="password"
              autoComplete="new-password"
              value={confirmPassword}
              onChange={e => { setConfirmPassword(e.target.value); setConfirmError(''); }}
              required
            />
          )}
        </Field>
        <Button type="submit" variant="primary" loading={isLoading}>
          {isLoading ? 'Creating account…' : 'Create account'}
        </Button>
      </form>
    </AuthLayout>
  );
}
